/**
 * What other people look like.
 *
 * This game is full of humanoid monsters — facelings, partygoers, mannequins,
 * a smiler's silhouette — and every one of them is built to be *almost* a
 * person. So the one figure in the building that genuinely is a person has to
 * be unmistakable at a glance, in fog, from across a dark room. Mistaking a
 * friend for a faceling, or a faceling for a friend, has to be a scare the game
 * chose, never an accident of rendering.
 *
 * Three things do that work, and they are all deliberate inversions of the
 * entity vocabulary:
 *
 *  - **Colour.** Every monster is drab: grey skin, dark trousers, colours the
 *    Lobby's wallpaper swallows. A remote player wears one saturated,
 *    non-yellow jacket and a reflective band. Nothing else in the building is
 *    that colour.
 *  - **A torch.** A light source moving through the level is the strongest
 *    "somebody is there" signal the game has, and no entity carries one. When
 *    their torch is on it is visible long before their body is.
 *  - **Being alive.** The faceling's whole trick is that nothing about it runs
 *    on a clock — it stops dead when watched. So a person breathes, shifts
 *    their weight, and looks around while standing still. Stillness is the
 *    monsters' tell; motion is ours.
 *
 * Everything here follows the entity-view conventions: geometry and materials
 * are shared through a reference-counted `Kit`, the figure is authored with the
 * floor at y = 0 and facing -Z, and the update path allocates nothing. The
 * whole avatar is a little over five hundred triangles.
 *
 * Interpolation. Positions arrive at `STATE_HZ` — fifteen times a second, far
 * below the frame rate — so nothing is ever drawn at a received position. Every
 * quantity is damped toward its target, and the walk cycle is driven by the
 * distance the *drawn* body has actually covered, so feet never slide. A large
 * jump (a descent, or a gap in the packets) snaps rather than gliding a body
 * through a wall.
 */

import * as THREE from 'three';
import { TUNING } from '../core/config';
import { clamp01, damp, forwardFromYaw, hash1, renderYaw, smoothstep, TAU } from '../core/math';
import { bone, castShadows, chain, dampAngle, freeAll, Kit, type Disposable } from './entkit';

/**
 * One other player, as the view needs them. Structurally identical to the
 * `RemotePeer` the transport publishes — deliberately re-declared rather than
 * imported, so the render layer keeps no dependency on the net layer and this
 * module can be driven by a test harness or a replay.
 */
export interface RemoteAvatar {
  id: string;
  x: number;
  z: number;
  /** Radians, the game's convention: 0 looks down -Z. */
  yaw: number;
  /** Their eye height. Carries crouch, and their head bob, which is filtered out. */
  eye: number;
  light: boolean;
  /** 0..100. Read as posture, never as a number. */
  health: number;
  /** Empty until the server has told us who they are. Then drawn, small and dim. */
  name: string;
}

/** The scene-graph handle plus everything the strike gesture needs to aim. */
export interface RemotePlayers {
  /** Parent this to the scene once. Everything lives under it. */
  readonly object: THREE.Object3D;
  /** Create, update and retire avatars to match `peers`. Call once a frame. */
  sync(peers: readonly RemoteAvatar[], dt: number, time: number, camera: THREE.Camera): void;
  /**
   * The drawn (interpolated) chest position of one peer. False when we have no
   * avatar for them, in which case `out` is untouched.
   */
  chestOf(id: string, out: THREE.Vector3): boolean;
  /**
   * The peer nearest the middle of the screen, within `range` metres and
   * `aimRadius` metres of the aim ray. Null when the crosshair is on nobody.
   * Players already down are never returned — you cannot hit somebody twice.
   */
  pick(camera: THREE.Camera, range: number, aimRadius: number): string | null;
  /**
   * How many remote torches may cast real light. Zero switches the lights off
   * entirely (the glow at the lens remains), which is what a low-end machine
   * or a quality setting should do.
   */
  setTorchLights(n: number): void;
  dispose(): void;
}

/* ------------------------------------------------------------------ *
 * Proportions. An ordinary adult, about 1.75 m, matching the player's own
 * standing eye height so a friend's head is where your head would be.
 * ------------------------------------------------------------------ */

const HIP_Y = 0.92;
const THIGH = 0.46;
const SHIN = 0.44;
/** Shoulder and head heights, measured from the hips. */
const SHOULDER_Y = 0.52;
const HEAD_Y = 0.7;
const UPPER_ARM = 0.29;
const FOREARM = 0.27;
/** Chest height above the floor: what the crosshair aims at and voice comes from. */
const CHEST_Y = HIP_Y + 0.34;

const STAND_EYE = TUNING.player.eyeHeight;
const CROUCH_EYE = TUNING.player.crouchEyeHeight;
/**
 * How far the hips drop at a full crouch.
 *
 * Deliberately short of the 0.73 m the player's own eye travels: a body whose
 * head is genuinely at 0.95 m with its feet under it is kneeling, not
 * crouching, and a kneeling avatar reads as something that has collapsed. Three
 * quarters of the distance plus the forward pitch of the torso lands the head
 * within about fifteen centimetres of where their camera actually is, and looks
 * like a person hiding.
 */
const CROUCH_DROP = (STAND_EYE - CROUCH_EYE) * 0.75;

/** Radians of gait per metre travelled — one full cycle every 1.4 m. */
const CADENCE = TAU / 1.4;
/** Metres per second below which the body is standing rather than walking. */
const STILL = 0.08;
/** A jump further than this in one update is a descent or a dropped packet: snap. */
const SNAP_DISTANCE = 3.5;

/** Damping rates. Tuned against a 15 Hz feed: high enough to keep up, low enough to smooth. */
const POS_RATE = 14;
const YAW_RATE = 11;
const CROUCH_RATE = 9;

/**
 * Jackets. Saturated and deliberately not yellow, because the building is: a
 * mono-yellow room swallows anything warm and mid-toned, which is precisely why
 * every entity is drab. Six is enough that a family of four never collides.
 */
const JACKETS: readonly number[] = [0xd8542a, 0x2f7ec4, 0x33a06a, 0xb8408c, 0x7d5ad8, 0xd93f52];

/** Metres past which a remote torch stops being worth a real light. */
const TORCH_LIGHT_RANGE = 26;
/** Default size of the torch light pool. See `ensureTorchPool` for why it is small. */
const DEFAULT_TORCH_LIGHTS = 2;

/** Name plates: metres at which they fade out, and how far off-centre they survive. */
const LABEL_NEAR = 7;
const LABEL_FAR = 13;
const LABEL_DOT_MIN = 0.72;
const LABEL_DOT_MAX = 0.9;
const LABEL_OPACITY = 0.72;
const LABEL_HEIGHT = 0.13;

/** Health at which the body starts to show it, and at which it is all it shows. */
const HURT_HEALTHY = 55;
const HURT_BAD = 15;

/** Scratch. The per-frame path must not allocate. */
const _v = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _to = new THREE.Vector3();

/** Stable per-peer variation: two people are never the same height or colour. */
function hashId(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/* ------------------------------------------------------------------ *
 * Shared GPU resources
 * ------------------------------------------------------------------ */

/** A soft radial falloff for the torch bloom. Same trick the smiler's eyes use. */
function makeGlowTexture(): THREE.Texture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.25, 'rgba(255,250,235,0.42)');
  g.addColorStop(0.6, 'rgba(255,245,220,0.1)');
  g.addColorStop(1, 'rgba(255,240,210,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

interface PersonRig {
  root: THREE.Group;
  /** Everything above the hips. Pitches forward when hurt. */
  torso: THREE.Group;
  head: THREE.Group;
  /** [left, right], each [thigh, shin]. */
  legs: THREE.Group[][];
  /** [left, right], each [upper, fore]. */
  arms: THREE.Group[][];
  /** The lit end of the torch, and its bloom. Hidden when their light is off. */
  lens: THREE.Mesh;
  glow: THREE.Sprite;
}

interface PersonKit extends Disposable {
  make(jacket: number): PersonRig;
}

function buildPersonKit(): PersonKit {
  // Lambert throughout, like every entity: a person has to be lit by the room
  // and by your torch, or they read as a decal pasted on the world.
  const skin = new THREE.MeshLambertMaterial({ color: 0xc9a288 });
  const trouser = new THREE.MeshLambertMaterial({ color: 0x3b4048 });
  const shoe = new THREE.MeshLambertMaterial({ color: 0x1d1d20 });
  // The hi-vis band. Bright enough to catch a torch beam from across a room,
  // and the single most human thing about the silhouette: no entity wears kit.
  const band = new THREE.MeshLambertMaterial({ color: 0xe8eae0, emissive: 0x2a2c26 });
  const dark = new THREE.MeshLambertMaterial({ color: 0x17181a });
  const eyeMat = new THREE.MeshBasicMaterial({ color: 0x14100e });
  // The torch lens itself: unlit and always at full strength, so it survives
  // the fog and is the first thing visible down a corridor.
  const lensMat = new THREE.MeshBasicMaterial({ color: 0xfff3d8, fog: false });
  const jackets = JACKETS.map((c) => new THREE.MeshLambertMaterial({ color: c }));

  const glowTexture = makeGlowTexture();
  const glowMat = new THREE.SpriteMaterial({
    map: glowTexture,
    color: 0xffeec2,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    // Depth-tested on purpose: a torch glow shining through a wall would turn
    // hide-and-seek into a wallhack.
    depthTest: true,
    blending: THREE.AdditiveBlending,
    fog: false,
  });

  const chestGeo = new THREE.CapsuleGeometry(0.16, 0.34, 2, 8);
  chestGeo.scale(1, 1, 0.72);
  chestGeo.translate(0, 0.3, 0);
  const shoulderGeo = new THREE.SphereGeometry(0.115, 7, 4);
  shoulderGeo.scale(1.7, 0.62, 0.9);
  shoulderGeo.translate(0, 0.5, 0);
  const hipGeo = new THREE.CapsuleGeometry(0.135, 0.1, 2, 7);
  hipGeo.scale(1, 1, 0.78);
  const bandGeo = new THREE.CylinderGeometry(0.172, 0.172, 0.055, 10, 1, true);
  bandGeo.scale(1, 1, 0.74);
  bandGeo.translate(0, 0.33, 0);
  const neckGeo = new THREE.CylinderGeometry(0.045, 0.055, 0.1, 6);
  neckGeo.translate(0, 0.6, 0);

  const headGeo = new THREE.SphereGeometry(0.105, 8, 6);
  headGeo.scale(0.92, 1.12, 1);
  // A woolly hat in the jacket colour. It is the reason the head reads as a
  // person's at fifteen metres, where the face is two pixels.
  const hatGeo = new THREE.SphereGeometry(0.112, 8, 4, 0, TAU, 0, Math.PI * 0.55);
  hatGeo.scale(1, 1.05, 1);
  hatGeo.translate(0, 0.022, 0);
  // Eyes: four triangles that mean "this one has a face". Facelings do not.
  // Turned to face -Z with the rest of the body, or backface culling eats them.
  const eyeGeo = new THREE.PlaneGeometry(0.026, 0.016);
  eyeGeo.rotateY(Math.PI);

  const legGeos = [bone(THIGH, 0.085, 0.065, 5), bone(SHIN, 0.062, 0.048, 5)];
  const shoeGeo = new THREE.BoxGeometry(0.085, 0.05, 0.2);
  shoeGeo.translate(0, -SHIN - 0.022, -0.04);

  const armGeos = [bone(UPPER_ARM, 0.055, 0.045, 5), bone(FOREARM, 0.044, 0.036, 5)];
  const handGeo = new THREE.BoxGeometry(0.055, 0.09, 0.06);
  handGeo.translate(0, -FOREARM - 0.05, 0);

  // The torch lies along the forearm, which is what a fist holding one does:
  // hanging by their side it points at the floor, and the carry pose that comes
  // with switching it on swings the forearm horizontal and the beam forward.
  const torchGeo = new THREE.CylinderGeometry(0.026, 0.03, 0.15, 6);
  torchGeo.translate(0, -0.075, 0);
  const lensGeo = new THREE.SphereGeometry(0.028, 6, 4);

  const geos = [
    chestGeo,
    shoulderGeo,
    hipGeo,
    bandGeo,
    neckGeo,
    headGeo,
    hatGeo,
    eyeGeo,
    ...legGeos,
    shoeGeo,
    ...armGeos,
    handGeo,
    torchGeo,
    lensGeo,
  ];

  return {
    make(jacket: number): PersonRig {
      const cloth = jackets[jacket % jackets.length]!;
      const root = new THREE.Group();

      const hips = new THREE.Mesh(hipGeo, trouser);
      hips.position.y = HIP_Y;
      root.add(hips);

      const torso = new THREE.Group();
      torso.position.y = HIP_Y;
      root.add(torso);
      torso.add(
        new THREE.Mesh(chestGeo, cloth),
        new THREE.Mesh(shoulderGeo, cloth),
        new THREE.Mesh(bandGeo, band),
        new THREE.Mesh(neckGeo, skin)
      );

      const head = new THREE.Group();
      head.position.y = HEAD_Y;
      head.add(new THREE.Mesh(headGeo, skin), new THREE.Mesh(hatGeo, cloth));
      for (const side of [-1, 1] as const) {
        const eye = new THREE.Mesh(eyeGeo, eyeMat);
        eye.position.set(side * 0.038, 0.022, -0.093);
        head.add(eye);
      }
      torso.add(head);

      const legs: THREE.Group[][] = [];
      for (const side of [-1, 1] as const) {
        const joints = chain(legGeos, [THIGH, SHIN], trouser);
        joints[0]!.position.set(side * 0.098, HIP_Y, 0);
        joints[1]!.add(new THREE.Mesh(shoeGeo, shoe));
        root.add(joints[0]!);
        legs.push(joints);
      }

      const arms: THREE.Group[][] = [];
      for (const side of [-1, 1] as const) {
        const joints = chain(armGeos, [UPPER_ARM, FOREARM], cloth);
        joints[0]!.position.set(side * 0.195, SHOULDER_Y, 0);
        joints[0]!.rotation.z = side * 0.08;
        joints[1]!.add(new THREE.Mesh(handGeo, skin));
        torso.add(joints[0]!);
        arms.push(joints);
      }

      // The torch is held in the right hand, at the end of the forearm.
      const hand = arms[1]![1]!;
      const torch = new THREE.Group();
      torch.position.set(0, -FOREARM - 0.05, -0.02);
      torch.add(new THREE.Mesh(torchGeo, dark));
      hand.add(torch);

      const lens = new THREE.Mesh(lensGeo, lensMat);
      lens.position.y = -0.158;
      torch.add(lens);

      // Shared material: the bloom is switched with `visible`, never faded per
      // person, so there is nothing here for two avatars to fight over.
      const glow = new THREE.Sprite(glowMat);
      glow.scale.setScalar(0.5);
      glow.position.y = -0.18;
      torch.add(glow);

      castShadows(root);
      // The lens and its bloom are light sources, not obstacles.
      lens.castShadow = false;
      glow.castShadow = false;
      return { root, torso, head, legs, arms, lens, glow };
    },
    dispose(): void {
      freeAll(geos);
      freeAll([skin, trouser, shoe, band, dark, eyeMat, lensMat, glowMat, glowTexture, ...jackets]);
    },
  };
}

const personKit = new Kit(buildPersonKit);

/* ------------------------------------------------------------------ *
 * Name plates
 * ------------------------------------------------------------------ */

interface LabelTexture {
  texture: THREE.CanvasTexture;
  /** Width / height of the drawn text, so the sprite is not stretched. */
  aspect: number;
  refs: number;
}

/**
 * One canvas per distinct name, shared and reference counted. Four family
 * members is four textures for the life of the page; a name that stops being
 * used is freed.
 */
const labelCache = new Map<string, LabelTexture>();

const LABEL_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

function acquireLabel(name: string): LabelTexture {
  const cached = labelCache.get(name);
  if (cached) {
    cached.refs++;
    return cached;
  }

  const px = 44;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  ctx.font = `${px}px ${LABEL_FONT}`;
  const width = Math.ceil(ctx.measureText(name).width) + px;
  canvas.width = Math.max(width, 8);
  canvas.height = Math.ceil(px * 1.6);

  const c = canvas.getContext('2d')!;
  c.font = `${px}px ${LABEL_FONT}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  // A soft dark halo rather than an outline: the name has to stay readable
  // against a lit wall without ever looking like UI stuck to the world.
  c.shadowColor = 'rgba(0,0,0,0.85)';
  c.shadowBlur = px * 0.35;
  c.fillStyle = 'rgba(238,236,226,0.95)';
  c.fillText(name, canvas.width / 2, canvas.height / 2);
  c.fillText(name, canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;

  const entry: LabelTexture = { texture, aspect: canvas.width / canvas.height, refs: 1 };
  labelCache.set(name, entry);
  return entry;
}

function releaseLabel(name: string): void {
  const entry = labelCache.get(name);
  if (!entry) return;
  if (--entry.refs > 0) return;
  entry.texture.dispose();
  labelCache.delete(name);
}

/* ------------------------------------------------------------------ *
 * One avatar
 * ------------------------------------------------------------------ */

class AvatarView {
  readonly object = new THREE.Group();
  readonly id: string;

  /** Interpolated, drawn position. Everything aims at this, not at the packet. */
  x: number;
  z: number;
  yaw: number;
  /** 1 while their torch is on and they are standing. Drives the light pool. */
  lit = 0;
  /** 0 alive, 1 collapsed. Kept public so `pick` can refuse to hit a body. */
  down = 0;
  /** Metres to the camera, cached by `sync` so the torch sort does not recompute it. */
  distance = 0;

  private readonly rig: PersonRig;
  private readonly scale: number;
  private readonly phase: number;

  private stride: number;
  private speed = 0;
  private crouch = 0;
  private hurt = 0;
  private carry = 0;
  private eyeSmoothed: number;

  private label: THREE.Sprite | null = null;
  private labelName = '';
  private labelTex: LabelTexture | null = null;

  constructor(peer: RemoteAvatar) {
    const kit = personKit.acquire();
    this.id = peer.id;
    const h = hashId(peer.id);
    this.rig = kit.make(h % JACKETS.length);
    // Height varies by a few centimetres either way. Fixed for life, so a
    // friend stays the same height across a room.
    this.scale = 0.95 + hash1(h, 0x51) * 0.11;
    this.phase = hash1(h, 0x77) * TAU;
    this.stride = hash1(h, 0x93) * TAU;
    this.rig.root.scale.setScalar(this.scale);
    this.object.add(this.rig.root);

    this.x = peer.x;
    this.z = peer.z;
    this.yaw = peer.yaw;
    this.eyeSmoothed = peer.eye;
    this.setLabel(peer.name);
  }

  /** Chest height in world units, after the crouch and the collapse. */
  get chestY(): number {
    return (CHEST_Y - this.crouch * CROUCH_DROP) * this.scale * (1 - this.down * 0.7);
  }

  private setLabel(name: string): void {
    if (name === this.labelName) return;
    if (this.label) {
      this.object.remove(this.label);
      (this.label.material as THREE.SpriteMaterial).dispose();
      this.label = null;
    }
    // Released against the *old* name, which is still what `labelName` holds.
    if (this.labelTex) {
      releaseLabel(this.labelName);
      this.labelTex = null;
    }
    this.labelName = name;
    if (!name) return;

    const entry = acquireLabel(name);
    this.labelTex = entry;
    const material = new THREE.SpriteMaterial({
      map: entry.texture,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      // Occluded by walls: a name floating through the wallpaper would tell
      // you where somebody is hiding, which is the whole game between friends.
      depthTest: true,
      fog: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(LABEL_HEIGHT * entry.aspect, LABEL_HEIGHT, 1);
    sprite.center.set(0.5, 0);
    this.label = sprite;
    this.object.add(sprite);
  }

  update(peer: RemoteAvatar, dt: number, time: number, camera: THREE.Camera): void {
    this.setLabel(peer.name);

    // Position. A jump bigger than a room is a descent or a lost packet — glide
    // and the body walks through the wall between here and there.
    const jump = Math.hypot(peer.x - this.x, peer.z - this.z);
    if (jump > SNAP_DISTANCE) {
      this.x = peer.x;
      this.z = peer.z;
      this.yaw = peer.yaw;
      this.speed = 0;
    } else {
      const px = this.x;
      const pz = this.z;
      this.x = damp(this.x, peer.x, POS_RATE, dt);
      this.z = damp(this.z, peer.z, POS_RATE, dt);
      this.yaw = dampAngle(this.yaw, peer.yaw, YAW_RATE, dt);
      // Speed comes off the *drawn* motion rather than off the packets, so the
      // gait always matches the distance the feet actually cover.
      const moved = Math.hypot(this.x - px, this.z - pz);
      this.speed = damp(this.speed, dt > 0 ? moved / dt : 0, 12, dt);
    }
    this.object.position.set(this.x, 0, this.z);
    this.object.rotation.y = renderYaw(this.yaw);

    // Crouch, from eye height. Heavily smoothed first: `eye` carries the
    // sender's head bob at 8.4 Hz, which a 15 Hz feed cannot represent — read
    // raw it would alias into a slow, drunken wobble.
    this.eyeSmoothed = damp(this.eyeSmoothed, peer.eye, 5, dt);
    const wantCrouch = clamp01((STAND_EYE - this.eyeSmoothed) / (STAND_EYE - CROUCH_EYE));
    this.crouch = damp(this.crouch, wantCrouch, CROUCH_RATE, dt);

    this.hurt = damp(this.hurt, 1 - smoothstep(HURT_BAD, HURT_HEALTHY, peer.health), 2.2, dt);
    this.down = damp(this.down, peer.health <= 0 ? 1 : 0, 3, dt);
    const alive = 1 - this.down;

    // A ramp rather than a switch: crossing a threshold would pop the bob and
    // snap the idle sway off, which is exactly the kind of discontinuity that
    // makes a body read as a puppet.
    const walking = clamp01((this.speed - STILL) / 0.35);
    this.stride += this.speed * dt * CADENCE * alive;
    // Idle breath and weight shift. This is the line between a person and an
    // entity: a faceling reads nothing off the clock and holds whatever pose it
    // stopped in, and a body that never moves at all is read as one.
    const breath = Math.sin(time * 1.15 + this.phase) * (1 - walking) * alive;
    const sway = Math.sin(time * 0.62 + this.phase) * (1 - walking) * alive;

    const rig = this.rig;
    const swing = (0.12 + clamp01(this.speed / 2.6) * 0.5) * alive;
    // Somebody badly hurt walks unevenly — the injured leg does less work.
    const limp = 1 - this.hurt * 0.45;

    for (let i = 0; i < 2; i++) {
      const p = this.stride + i * Math.PI;
      const s = i === 0 ? swing * limp : swing;
      const leg = rig.legs[i]!;
      // Knees stay bent while crouched, which is what makes a crouching figure
      // read as crouching rather than as a short person.
      const bend = this.crouch * 1.15;
      leg[0]!.rotation.x = Math.sin(p) * s + bend * 0.85;
      leg[1]!.rotation.x = -Math.max(0, Math.sin(p + 0.7)) * s * 1.05 - 0.05 - bend * 1.7;
    }

    // Arms. Positive `rotation.x` swings a limb forward (a joint hangs down its
    // own -Y and the body faces -Z), so every bend below is a forward one — an
    // elbow that opens backwards is the single fastest way to turn a person
    // into one of the things in the building.
    //
    // The torch arm stops swinging and comes up to carry: forearm horizontal,
    // which is both how somebody walks with a torch and what points the beam
    // where they are looking.
    this.carry = damp(this.carry, peer.light ? 1 : 0, 6, dt);
    for (let i = 0; i < 2; i++) {
      const p = this.stride + i * Math.PI + Math.PI;
      const arm = rig.arms[i]!;
      const side = i === 0 ? -1 : 1;
      const free = Math.sin(p) * swing * 0.55;
      const rest = 0.16 + Math.max(0, free) * 0.4;
      if (i === 1) {
        arm[0]!.rotation.x = free * (1 - this.carry) + this.carry * 0.45;
        arm[1]!.rotation.x = rest * (1 - this.carry) + this.carry * 1.1;
        arm[0]!.rotation.z = side * (0.08 - this.carry * 0.05);
      } else {
        // A hurt player holds their off arm across their body: forward, bent,
        // and pulled inward across the belly.
        arm[0]!.rotation.x = free * (1 - this.hurt) + this.hurt * 0.55;
        arm[1]!.rotation.x = rest * (1 - this.hurt) + this.hurt * 1.1;
        arm[0]!.rotation.z = side * 0.08 * (1 - this.hurt) + this.hurt * 0.16;
      }
    }

    // Hips and vertical. The crouch drop is measured against the player's own
    // eye heights, so a crouching friend's head is about where yours would be.
    const crouchDrop = this.crouch * CROUCH_DROP;
    const bob = (Math.abs(Math.sin(this.stride)) - 0.5) * 0.03 * walking;
    // The limp lands here too: a dip on the bad leg's stance.
    const hitch = this.hurt * 0.035 * Math.max(0, Math.sin(this.stride)) * walking;
    rig.root.position.y = (bob - crouchDrop - hitch + breath * 0.006) * alive - this.down * 0.3;
    rig.root.rotation.z = (sway * 0.02 + Math.sin(this.stride) * 0.03 * walking) * alive + this.down * 1.45;

    rig.torso.rotation.x = 0.03 + this.crouch * 0.34 + this.hurt * 0.3;
    rig.torso.rotation.z = sway * 0.03;
    // The head stays level while the body hunches, and drifts while idle: the
    // most human thing in the whole rig is somebody looking around.
    rig.head.rotation.x = -this.crouch * 0.2 - this.hurt * 0.2 + breath * 0.02;
    rig.head.rotation.y = sway * 0.22;

    rig.lens.visible = peer.light && this.down < 0.5;
    rig.glow.visible = rig.lens.visible;

    this.lit = peer.light ? 1 - this.down : 0;
    this.updateLabel(camera);
  }

  /** Small, dim, and only when they are close and roughly in front of you. */
  private updateLabel(camera: THREE.Camera): void {
    const label = this.label;
    if (!label) return;
    const material = label.material as THREE.SpriteMaterial;

    _to.set(this.x - camera.position.x, this.chestY - camera.position.y, this.z - camera.position.z);
    const dist = _to.length() || 1e-4;
    _to.multiplyScalar(1 / dist);
    // The camera's local orientation, not its world matrix: the renderer parents
    // the camera at the scene root and has not refreshed matrices this frame.
    _fwd.set(0, 0, -1).applyQuaternion(camera.quaternion);

    const near = 1 - smoothstep(LABEL_NEAR, LABEL_FAR, dist);
    const centred = smoothstep(LABEL_DOT_MIN, LABEL_DOT_MAX, _to.dot(_fwd));
    material.opacity = near * centred * LABEL_OPACITY;
    label.visible = material.opacity > 0.01;
    // A hand's width above the crown, following the crouch down.
    const crown = HIP_Y + HEAD_Y + 0.24 - this.crouch * CROUCH_DROP;
    label.position.set(0, crown * this.scale * (1 - this.down * 0.8), 0);
  }

  dispose(): void {
    if (this.label) {
      (this.label.material as THREE.SpriteMaterial).dispose();
      this.object.remove(this.label);
      this.label = null;
    }
    if (this.labelTex) {
      releaseLabel(this.labelName);
      this.labelTex = null;
    }
    this.object.clear();
    personKit.release();
  }
}

/* ------------------------------------------------------------------ *
 * The set of them
 * ------------------------------------------------------------------ */

interface TorchSlot {
  light: THREE.SpotLight;
  target: THREE.Object3D;
}

/** Module-level so the per-frame torch sort allocates no closure. */
function byDistance(a: AvatarView, b: AvatarView): number {
  return a.distance - b.distance;
}

class Players implements RemotePlayers {
  readonly object = new THREE.Group();

  private readonly views = new Map<string, AvatarView>();
  private readonly seen = new Set<string>();
  private readonly torches: TorchSlot[] = [];
  private torchCount = DEFAULT_TORCH_LIGHTS;
  /** Reused for the "which torches are worth a light" sort. */
  private readonly candidates: AvatarView[] = [];

  constructor() {
    this.object.name = 'remote-players';
  }

  sync(peers: readonly RemoteAvatar[], dt: number, time: number, camera: THREE.Camera): void {
    this.seen.clear();
    for (const peer of peers) {
      this.seen.add(peer.id);
      let view = this.views.get(peer.id);
      if (!view) {
        view = new AvatarView(peer);
        this.views.set(peer.id, view);
        this.object.add(view.object);
      }
      view.update(peer, dt, time, camera);
      view.distance = Math.hypot(view.x - camera.position.x, view.z - camera.position.z);
    }

    if (this.views.size !== this.seen.size) {
      for (const [id, view] of this.views) {
        if (this.seen.has(id)) continue;
        this.object.remove(view.object);
        view.dispose();
        this.views.delete(id);
      }
    }

    this.updateTorches();
  }

  /**
   * Hand the torch lights to the nearest people carrying one.
   *
   * The pool is built the first time anybody's torch is actually in range, and
   * never taken down: changing the number of lights in a scene makes three
   * recompile every material in it, so a single-player session must never pay
   * for this, and a session with friends in it must pay exactly once.
   */
  private updateTorches(): void {
    const wanted = this.torchCount;
    const candidates = this.candidates;
    candidates.length = 0;

    if (wanted > 0) {
      for (const view of this.views.values()) {
        if (view.lit < 0.5 || view.distance > TORCH_LIGHT_RANGE) continue;
        candidates.push(view);
      }
      if (candidates.length > 1) candidates.sort(byDistance);
      if (candidates.length > 0 && this.torches.length === 0) this.ensureTorchPool();
    }

    for (let i = 0; i < this.torches.length; i++) {
      const slot = this.torches[i]!;
      const view = i < wanted ? candidates[i] : undefined;
      if (!view) {
        slot.light.intensity = 0;
        continue;
      }
      const fwd = forwardFromYaw(view.yaw);
      // The lens, derived rather than read off the scene graph: the rig's world
      // matrices belong to last frame, and a beam a frame behind its owner
      // swings visibly when they turn. These offsets are the carry pose
      // measured — forearm horizontal, torch out front in the right hand — and
      // the test in the harness holds them to within a few centimetres of where
      // the mesh actually puts it.
      const hx = view.x + fwd.x * 0.58 + Math.cos(view.yaw) * 0.2;
      const hz = view.z + fwd.z * 0.58 + Math.sin(view.yaw) * 0.2;
      const hy = view.chestY - 0.05;
      slot.light.position.set(hx, hy, hz);
      slot.target.position.set(hx + fwd.x * 4, hy - 0.35, hz + fwd.z * 4);
      slot.light.intensity = TUNING.flashlight.intensity * 2.4;
    }
  }

  private ensureTorchPool(): void {
    for (let i = 0; i < DEFAULT_TORCH_LIGHTS; i++) {
      const light = new THREE.SpotLight(
        0xfff2dc,
        0,
        TUNING.flashlight.range,
        TUNING.flashlight.coneAngle * 1.1,
        0.5,
        1.4
      );
      light.castShadow = false;
      const target = new THREE.Object3D();
      light.target = target;
      this.object.add(light, target);
      this.torches.push({ light, target });
    }
  }

  setTorchLights(n: number): void {
    this.torchCount = Math.max(0, Math.min(n | 0, DEFAULT_TORCH_LIGHTS));
  }

  chestOf(id: string, out: THREE.Vector3): boolean {
    const view = this.views.get(id);
    if (!view) return false;
    out.set(view.x, view.chestY, view.z);
    return true;
  }

  pick(camera: THREE.Camera, range: number, aimRadius: number): string | null {
    _fwd.set(0, 0, -1).applyQuaternion(camera.quaternion);
    let best: string | null = null;
    let bestOff = Infinity;

    for (const view of this.views.values()) {
      // Somebody already on the floor is not a target.
      if (view.down > 0.4) continue;
      _v.set(view.x - camera.position.x, view.chestY - camera.position.y, view.z - camera.position.z);
      const along = _v.dot(_fwd);
      if (along <= 0 || along > range) continue;
      // Distance from the aim ray. A metre of tolerance at arm's length would
      // let you hit somebody standing beside you; this is a chest, aimed at.
      const off = Math.sqrt(Math.max(0, _v.lengthSq() - along * along));
      if (off > aimRadius || off >= bestOff) continue;
      bestOff = off;
      best = view.id;
    }
    return best;
  }

  dispose(): void {
    for (const view of this.views.values()) {
      this.object.remove(view.object);
      view.dispose();
    }
    this.views.clear();
    for (const slot of this.torches) {
      this.object.remove(slot.light, slot.target);
      slot.light.dispose();
    }
    this.torches.length = 0;
    this.object.removeFromParent();
    this.object.clear();
  }
}

/**
 * Build the remote-player layer. Costs nothing until somebody appears: the
 * shared geometry is built on the first avatar and freed with the last one, and
 * the torch lights only exist once a lit torch is actually in the room.
 */
export function makeRemotePlayers(): RemotePlayers {
  return new Players();
}
